/**
 * Pure derivations for the Batch Remediation tab.
 *
 * Everything here reads the response of GET /api/batch-remediation/{app} and
 * the preview/trigger payloads. Nothing is re-derived from the JIRA ticket
 * list: the server buckets by Bedrock's verdict and acts on that same set, so
 * a second client-side grouping could only disagree with what the trigger
 * does.
 *
 * Kept as a plain module (no React, no CSS import) so it can be unit tested
 * directly, mirroring infraSummary.js and its __tests__ suite.
 */

export const BUCKET_UNCATEGORIZED = 'UNCATEGORIZED';

// Worst first, with the honest unknown last - the same order the server's
// batch_store.ALL_BUCKETS uses.
export const BUCKET_ORDER = ['HIGH', 'MEDIUM', 'LOW', BUCKET_UNCATEGORIZED];

// UNCATEGORIZED is deliberately neutral grey, not amber: it is the absence of
// an assessment, not a severity, and must never read as one.
export const BUCKET_STYLE = {
  HIGH: { color: '#dc2626', icon: '🔴', label: 'High' },
  MEDIUM: { color: '#ca8a04', icon: '🟡', label: 'Medium' },
  LOW: { color: '#16a34a', icon: '🟢', label: 'Low' },
  [BUCKET_UNCATEGORIZED]: { color: '#6b7280', icon: '⚪', label: 'Not yet assessed' },
};

// Epic filter sentinels. EPIC_ALL is the unfiltered view; EPIC_UNASSIGNED is
// the server's literal bucket key for a ticket with no parent.
export const EPIC_ALL = '';
export const EPIC_UNASSIGNED = 'UNASSIGNED';

const arr = (v) => (Array.isArray(v) ? v : []);
const int = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const trimmed = (v) => String(v === null || v === undefined ? '' : v).trim();

/**
 * The four bucket sections, tolerating a null response or a missing bucket.
 *
 * `triggerable` defaults to false when the server omits it: a response we
 * cannot read as explicit permission must not be treated as permission to
 * bulk-remediate.
 */
export const normalizeBuckets = (data) => {
  const src = (data && data.buckets) || {};
  const out = {};
  BUCKET_ORDER.forEach((name) => {
    const section = src[name] || {};
    const tickets = arr(section.tickets);
    const count = int(section.count);
    out[name] = {
      count: count === null ? tickets.length : count,
      tickets,
      triggerable: section.triggerable === true,
    };
  });
  return out;
};

/**
 * Epic keys to offer in the filter, in the server's configured order.
 *
 * Configured epics come first (so an epic with zero open tickets is still
 * selectable), then any epic present in `byEpic` but not configured, and
 * UNASSIGNED last because it is a catch-all rather than a real epic.
 */
export const epicOptions = (data) => {
  const byEpic = (data && data.byEpic) || {};
  const out = [];
  const push = (raw) => {
    const key = trimmed(raw);
    if (!key || key === EPIC_UNASSIGNED || out.includes(key)) return;
    out.push(key);
  };
  arr(data && data.epics).forEach(push);
  Object.keys(byEpic).forEach(push);
  const hasUnassigned = Object.prototype.hasOwnProperty.call(byEpic, EPIC_UNASSIGNED)
    || arr(data && data.epics).some((e) => trimmed(e) === EPIC_UNASSIGNED);
  if (hasUnassigned) out.push(EPIC_UNASSIGNED);
  return out;
};

/** Per-epic ticket total, or null when the server reported none for that key. */
export const epicTotal = (data, epic) => {
  const entry = ((data && data.byEpic) || {})[epic];
  return entry ? int(entry.total) : null;
};

/**
 * Filter an already-loaded ticket list by epic, with no second request.
 *
 * EPIC_ALL passes everything; EPIC_UNASSIGNED matches a missing or blank
 * parentKey, which is the same rule the server used to count it.
 */
export const filterTicketsByEpic = (tickets, epic) => {
  const list = arr(tickets);
  if (!epic) return list;
  if (epic === EPIC_UNASSIGNED) return list.filter((t) => !trimmed(t && t.parentKey));
  return list.filter((t) => trimmed(t && t.parentKey) === epic);
};

/**
 * Count for one bucket, from `byEpic` when an epic is selected and from the
 * top-level buckets otherwise. Both come from the same server pass, so the
 * filtered and unfiltered views cannot disagree.
 */
export const bucketCount = (data, name, epic) => {
  if (epic) {
    const entry = ((data && data.byEpic) || {})[epic] || {};
    const scoped = int(entry[name]);
    return scoped === null ? 0 : scoped;
  }
  const section = ((data && data.buckets) || {})[name] || {};
  const count = int(section.count);
  return count === null ? arr(section.tickets).length : count;
};

/**
 * Assessment coverage for the whole application or one epic.
 *
 * Returns null when there is no total to report (``hasData: false`` nulls the
 * counts), so the caller can stay silent rather than claim "0 of 0".
 */
export const coverage = (data, epic) => {
  const source = epic ? ((data && data.byEpic) || {})[epic] : data;
  const total = int(source && source.total);
  if (total === null) return null;
  const categorized = int(source && source.categorized);
  const uncategorized = int(source && source.uncategorized);
  const assessed = categorized === null ? 0 : categorized;
  return {
    total,
    categorized: assessed,
    uncategorized: uncategorized === null ? Math.max(total - assessed, 0) : uncategorized,
  };
};

/**
 * Classify a preview response so the panel never draws an unexplained zero.
 *
 *   'no-plan'        the server returned no usable plan - treat as an error
 *   'empty-bucket'   the server's bucket for this severity holds no tickets
 *   'all-excluded'   every ticket is in flight or already remediated
 *   'cap-exhausted'  eligible tickets exist but today's allowance is spent
 *   'no-repositories' eligible tickets exist but no repository resolved
 *   'ok'             there is something to open a pull request for
 *
 * A capped-but-non-empty selection is still 'ok': the run will act, just on
 * fewer tickets. That truncation is reported by :func:`capSummary` instead,
 * because it is a quantity, not an outcome.
 */
export const previewOutcome = (preview) => {
  if (!preview || !preview.runId) return 'no-plan';
  if (arr(preview.tickets).length === 0) return 'empty-bucket';
  const eligible = arr(preview.eligibleTickets).length;
  if (eligible === 0) return 'all-excluded';
  const selected = preview.selectedTickets;
  if (Array.isArray(selected) && selected.length === 0) return 'cap-exhausted';
  if (arr(preview.repositories).length === 0) return 'no-repositories';
  return 'ok';
};

/**
 * True when the server reports a run already ran today for this bucket.
 *
 * Confirming without force would be refused by the one-run-per-day guard, so
 * the UI uses this to warn up front and switch the confirm button to an
 * explicit force action rather than letting the operator click through to a
 * 409.
 */
export const alreadyRanToday = (preview) => !!(preview && preview.alreadyRanToday);

/**
 * Classify one repository's run outcome.
 *
 * 'plan-failed' is kept distinct from 'failed': the first means Bedrock could
 * not produce a plan so nothing was attempted, the second means the Copilot
 * run itself failed. Different cause, different next step.
 */
export const runOutcome = (repo) => {
  if (!repo) return 'failed';
  if (repo.planStatus === 'failed') return 'plan-failed';
  return repo.success ? 'ok' : 'failed';
};

/**
 * The daily-cap picture for a preview or run payload, or null when the payload
 * carries no `dailyCap` block (an older Lambda) so nothing is rendered.
 */
export const capSummary = (payload) => {
  if (!payload || !payload.dailyCap) return null;
  const app = payload.dailyCap.application || {};
  const selected = arr(payload.selectedTickets).length;
  const eligible = arr(payload.eligibleTickets).length;
  return {
    limit: int(app.limit),
    used: int(app.used) === null ? 0 : app.used,
    remaining: int(app.remaining),
    selected,
    eligible,
    deferred: arr(payload.deferredTickets).length,
    capped: payload.capped === true,
    byEpic: payload.dailyCap.byEpic || {},
  };
};

// ---------------------------------------------------------------------------
// Run progress
// ---------------------------------------------------------------------------

/**
 * Human-readable labels for a ticket's remediation status.
 *
 * `in_progress` is derived by the server for a ticket in the active run that
 * has no record of its own yet: the agent writes one only once Copilot accepts
 * that repository's delegation, minutes into the run.
 *
 * An unmapped value is shown verbatim rather than relabelled - a status we do
 * not recognise is better read as-is than hidden behind a guess.
 */
export const REMEDIATION_STATUS = {
  in_progress: { label: 'Remediation in progress', tone: 'running' },
  analyzing: { label: 'Analyzing', tone: 'running' },
  generating_fix: { label: 'Generating the fix', tone: 'running' },
  pr_created: { label: 'Pull request opened', tone: 'done' },
  pr_merged: { label: 'Pull request merged', tone: 'done' },
  verified: { label: 'Verified', tone: 'done' },
  pr_closed: { label: 'Pull request closed without merging', tone: 'warn' },
  superseded: { label: 'Superseded by a later run', tone: 'neutral' },
  skipped: { label: 'Skipped — no code change needed', tone: 'warn' },
  failed: { label: 'Failed', tone: 'bad' },
};

/** `{ label, tone }` for a status, or null when there is nothing to show. */
export const statusBadge = (status) => {
  const key = trimmed(status);
  if (!key) return null;
  return REMEDIATION_STATUS[key] || { label: key, tone: 'neutral' };
};

/**
 * The server's in-flight run, normalized, or null when nothing is running.
 *
 * `stale` is kept distinct from `running`: it means the worker stopped
 * reporting rather than finished, so the client must stop polling and say so
 * instead of leaving a spinner up indefinitely.
 */
export const activeRunState = (data) => {
  const run = data && data.activeRun;
  if (!run || !trimmed(run.runId)) return null;
  const status = trimmed(run.runStatus) || 'running';
  return {
    bucket: trimmed(run.bucket),
    runId: trimmed(run.runId),
    status,
    running: status === 'running',
    stale: status === 'stale',
    startedAt: run.startedAt || null,
    ageSeconds: int(run.ageSeconds),
    triggeredBy: trimmed(run.triggeredBy),
    branchHint: trimmed(run.branchHint),
    tickets: arr(run.tickets).map((k) => trimmed(k)).filter(Boolean),
    ticketCount: int(run.ticketCount) === null
      ? arr(run.tickets).length
      : run.ticketCount,
  };
};

/** Round a run's age down to a short "4m 20s"-style string. */
export const formatAge = (seconds) => {
  const total = int(seconds);
  if (total === null || total < 0) return null;
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
};

/** Deferred ticket count per epic, for the cap note next to the epic filter. */
export const deferredByEpic = (payload) => {
  const out = {};
  arr(payload && payload.deferredTickets).forEach((d) => {
    const epic = trimmed(d && d.epic) || EPIC_UNASSIGNED;
    out[epic] = (out[epic] || 0) + 1;
  });
  return out;
};
